import {ACCOUNT_DELETION_PENDING_MESSAGE, accountDeletionApi} from '@/api/account-deletion';
import {ApiClientError} from '@/api/client';
import {Button, Card, CardContent, CardHeader, CardTitle, Input, Label} from '@tryghost/shade';
import {FormEvent, useEffect, useState} from 'react';
import {authApi} from '@/api/auth';
import {getSites} from '@/api/sites';
import {useAuth} from '@/contexts/AuthContext';

export function DeleteAccountPage() {
    const {user} = useAuth();
    const [sites, setSites] = useState<Array<{id: string; name: string}>>([]);
    const [sitesError, setSitesError] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reauth, setReauth] = useState(false);
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [conflict, setConflict] = useState(false);
    const [linkSent, setLinkSent] = useState(false);
    const [result, setResult] = useState<'pending' | 'deleted' | null>(null);
    const [signOutError, setSignOutError] = useState(false);

    useEffect(() => {
        let active = true;
        getSites().then((data) => {
            if (active) {
                setSites(data.filter(site => site.access_type === 'owner'));
            }
        }).catch(() => {
            if (active) {
                setSitesError('We could not load your publications. All publications you own will still be deleted.');
            }
        });
        return () => {
            active = false;
        };
    }, []);

    const signOut = async (outcome: 'pending' | 'deleted') => {
        setSignOutError(false);
        try {
            await authApi.signOut();
            window.location.replace(`/login?${outcome === 'pending' ? 'deletionPending' : 'deleted'}=1`);
        } catch {
            setSignOutError(true);
        }
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (confirmation !== 'DELETE' || busy || conflict) {
            return;
        }
        setBusy(true);
        setError('');
        setPasswordError('');
        try {
            const response = await accountDeletionApi.request(showPassword && password ? password : undefined);
            const outcome = response?.status === 'deletion_pending' ? 'pending' : 'deleted';
            setResult(outcome);
            await signOut(outcome);
        } catch (err) {
            let message = err instanceof Error ? err.message : 'Could not request account deletion. Please try again.';
            if (err instanceof ApiClientError && err.code === 'REAUTH_REQUIRED') {
                setReauth(true);
            } else if (err instanceof ApiClientError && err.status === 401 && showPassword && password) {
                setPasswordError(message);
            } else if (err instanceof ApiClientError && err.status === 409) {
                setConflict(['SUPERADMIN_CANNOT_DELETE', 'AWAITING_REVIEW', 'ACCOUNT_DELETION_PENDING', 'ACTIVE_PAID_PLAN'].includes(err.code || ''));
                if (err.code === 'ACCOUNT_LOCK_BUSY') {
                    message = 'Try again in a moment.';
                }
            }
            setError(message);
        } finally {
            setBusy(false);
        }
    };

    const sendLink = async () => {
        if (!user?.email) {
            return;
        }
        setBusy(true);
        setError('');
        try {
            await authApi.sendSignInLink(user.email, '/settings/delete-account');
            setLinkSent(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not send the sign-in link.');
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return <section aria-label="Account deletion request" className="space-y-6">
            <p role="status">{result === 'pending' ? ACCOUNT_DELETION_PENDING_MESSAGE : 'Your account has been deleted.'}</p>
            {signOutError && <><p role="alert">Your request was accepted, but we could not sign you out.</p>
                <Button onClick={() => void signOut(result)}>Sign out</Button></>}
        </section>;
    }

    return <section aria-labelledby="delete-account-title" className="mx-auto max-w-2xl space-y-6">
        <h1 id="delete-account-title">Account settings</h1>
        <p>{user?.email}</p>
        <Card className="border-red-500">
            <CardHeader><CardTitle>Delete account</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <p>Deleting your account permanently removes every publication you own, including posts, members and subscribers, settings and custom domain connections, your team memberships on other people&apos;s publications, and your themes.</p>
                <p>Payment records are kept with your name and email removed. This is permanent and cannot be undone.</p>
                <p>{ACCOUNT_DELETION_PENDING_MESSAGE.replace('We\'ve received your request. ', '')}</p>
                <h2>Your publications</h2>
                {sitesError ? <p role="alert">{sitesError}</p> : <ul>{sites.map(site => <li key={site.id}>{site.name}</li>)}</ul>}
                <form className="space-y-4" onSubmit={submit}>
                    <Label htmlFor="delete-confirm">Type DELETE to confirm</Label>
                    <Input autoComplete="off" disabled={busy || conflict} id="delete-confirm" value={confirmation} onChange={event => setConfirmation(event.target.value)} />
                    {!showPassword && <Button type="button" variant="link" onClick={() => setShowPassword(true)}>Use my password</Button>}
                    {showPassword && <div className="space-y-2">
                        <Label htmlFor="delete-password">Password (if your account has one)</Label>
                        <Input aria-describedby={passwordError ? 'delete-password-error' : undefined} aria-invalid={!!passwordError} autoComplete="current-password" id="delete-password" type="password" value={password} onChange={event => setPassword(event.target.value)} />
                        {passwordError && <p id="delete-password-error" role="alert">{passwordError}</p>}
                    </div>}
                    {error && !passwordError && <p role="alert">{error}</p>}
                    {conflict && <p><a href="mailto:support@tribetale.com">Contact support@tribetale.com</a></p>}
                    {reauth && <Button disabled={busy || linkSent} type="button" variant="outline" onClick={() => void sendLink()}>Email me a sign-in link</Button>}
                    {linkSent && <p role="status">Check your email. The sign-in link will return you here to confirm deletion.</p>}
                    <Button disabled={confirmation !== 'DELETE' || busy || conflict} type="submit" variant="destructive">{busy ? 'Please wait…' : 'Delete account'}</Button>
                </form>
            </CardContent>
        </Card>
    </section>;
}
